/**
 * Tool Base Class
 *
 * Unified base class for all tools that combines:
 * - LLM function calling
 * - Streaming operation parsing
 * - System prompt description (for inclusion in agent prompts)
 *
 * Tools that don't need streaming can just leave fieldsToCheck empty.
 *
 * ## System Prompt Description
 *
 * Each tool defines a single string description for agent system prompts:
 *
 * ```typescript
 * class MyTool extends ToolBase<MyParams, MyResult> {
 *     public readonly systemPromptDescription = `### myTool
 * Use this tool to...
 *
 * **When to use:**
 * - Use case 1
 * - Use case 2`;
 * }
 * ```
 *
 * @template TParams - The expected parameter type (for documentation/type inference)
 * @template TResponse - The response type returned by the tool
 */
import { ToolDescription } from "./toolDescription";
// ═══════════════════════════════════════════════════════════════════════════
// TOOL BASE CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Base class for all tools.
 * Combines LLM function calling + streaming operation parsing.
 *
 * @example
 * // Tool with streaming support
 * class MyTool extends ToolBase<MyParams, MyResponse> {
 *     readonly name = "myTool";
 *     readonly description = new ToolDescription(...);
 *     fieldsToCheck = [
 *         { name: "query", type: "string", list_status: false, order: 1 }
 *     ];
 *
 *     async execute(params: unknown) { ... }
 * }
 *
 * @example
 * // Tool without streaming (empty arrays)
 * class SimpleTool extends ToolBase<SimpleParams, SimpleResponse> {
 *     readonly name = "simple";
 *     readonly description = new ToolDescription(...);
 *     fieldsToCheck = []; // No streaming needed
 *
 *     async execute(params: unknown) { ... }
 * }
 */
export class ToolBase {
    constructor() {
        // ═══════════════════════════════════════════════════════════════════════
        // REQUIRED PROPERTIES - Must be set by each tool
        // ═══════════════════════════════════════════════════════════════════════
        /** Tool name (used for LLM function calling AND streaming) */
        this.name = "";
        /** Tool description for LLM function calling */
        this.description = new ToolDescription("", "", {});
        // ═══════════════════════════════════════════════════════════════════════
        // STREAMING OPERATION (optional - empty arrays if not needed)
        // ═══════════════════════════════════════════════════════════════════════
        /** Single-value fields to extract during streaming */
        this.fieldsToCheck = [];
        /** Array fields where items are committed individually */
        this.listsToCheck = [];
        // ═══════════════════════════════════════════════════════════════════════
        // STATE (for streaming)
        // ═══════════════════════════════════════════════════════════════════════
        /** Extracted data from parsed fields */
        this.extractedData = {};
        /** Track if operation is complete */
        this._isComplete = false;
        this.isAgentTool = false;
    }
    /**
     * Get the tool's description for inclusion in agent system prompts.
     *
     * @returns The tool's system prompt description
     */
    getSystemPromptDescription() {
        return this.systemPromptDescription || `### ${this.name}\n[No description provided]`;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CALLBACKS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Subscribe to field updates (for real-time UI updates)
     *
     * Called each time a field value stabilizes during streaming.
     */
    setOnFieldUpdate(callback) {
        this.onFieldUpdate = callback;
        return this;
    }
    /**
     * Subscribe to operation completion
     *
     * Called when all expected fields have been parsed.
     */
    setOnComplete(callback) {
        this.onComplete = callback;
        return this;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CORE - Called by handleStreamChunk
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Add a parsed field value
     *
     * Called by handleStreamChunk when a field value stabilizes
     * (passes on-deck validation).
     *
     * @param fieldName - Name of the field
     * @param value - Parsed value
     */
    addStep(fieldName, value) {
        const isListField = this.isListField(fieldName);
        if (isListField) {
            // For list fields, append to array
            const existing = this.extractedData[fieldName] || [];
            existing.push(value);
            this.extractedData[fieldName] = existing;
        }
        else {
            // For single-value fields, set directly
            this.extractedData[fieldName] = value;
        }
        // Notify subscriber
        this.onFieldUpdate?.(fieldName, value, isListField);
        // Check if all fields are complete
        this.checkCompletion();
        console.log(`[${this.name}] addStep: ${fieldName} =`, value);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // DATA ACCESS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get all extracted data
     */
    getExtractedData() {
        return { ...this.extractedData };
    }
    /**
     * Get a specific field value
     */
    getField(fieldName) {
        return this.extractedData[fieldName];
    }
    /**
     * Check if operation is complete (all required fields parsed)
     */
    isComplete() {
        return this._isComplete;
    }
    /**
     * Reset operation state for reuse
     */
    reset() {
        this.extractedData = {};
        this._isComplete = false;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // NON-STREAMING MODE
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Commit all arguments at once (for non-streaming mode)
     *
     * When streaming is disabled, call this with the fully parsed
     * arguments to populate all fields at once.
     *
     * @param args - Fully parsed tool arguments
     */
    commitAll(args) {
        // Process all configured fields
        for (const field of this.fieldsToCheck) {
            if (field.name in args) {
                this.addStep(field.name, args[field.name]);
            }
        }
        // Process all configured list fields
        for (const field of this.listsToCheck) {
            if (field.name in args && Array.isArray(args[field.name])) {
                const items = args[field.name];
                for (const item of items) {
                    this.addStep(field.name, item);
                }
            }
        }
        // Mark as complete
        this._isComplete = true;
        this.onComplete?.(this.extractedData);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // UTILITIES
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Check if a field is a list field
     */
    isListField(fieldName) {
        return this.listsToCheck.some(f => f.name === fieldName);
    }
    /**
     * Check if all required fields have been parsed
     */
    checkCompletion() {
        if (this._isComplete)
            return;
        // Check all single-value fields
        const allFieldsComplete = this.fieldsToCheck.every(field => field.name in this.extractedData);
        // For list fields, we can't know when they're "complete" during streaming
        // So we only mark complete if there are no list fields, or in commitAll
        const hasListFields = this.listsToCheck.length > 0;
        if (allFieldsComplete && !hasListFields) {
            this._isComplete = true;
            this.onComplete?.(this.extractedData);
        }
    }
    /**
     * Get field configuration by name
     */
    getFieldConfig(fieldName) {
        return this.fieldsToCheck.find(f => f.name === fieldName)
            || this.listsToCheck.find(f => f.name === fieldName);
    }
    /**
     * Get all field names
     */
    getAllFieldNames() {
        return [
            ...this.fieldsToCheck.map(f => f.name),
            ...this.listsToCheck.map(f => f.name),
        ];
    }
    /**
     * Check if this tool supports streaming (has configured fields)
     */
    hasStreamingSupport() {
        return this.fieldsToCheck.length > 0 || this.listsToCheck.length > 0;
    }
    toRuntimeToolDefinition() {
        if (!this.name || this.name.trim().length === 0) {
            throw new Error("ToolBase.toRuntimeToolDefinition: tool name is required.");
        }
        return {
            name: this.name,
            description: this.description.function.description,
            schema: this.description.function.parameters,
            paramsSchema: this.paramsSchema,
            systemPromptDescription: this.systemPromptDescription,
            isAgentTool: this.isAgentTool,
            fieldsToCheck: this.fieldsToCheck,
            listsToCheck: this.listsToCheck,
            streamableOperation: this,
            execute: async (params) => this.execute(params),
        };
    }
}
/**
 * Type guard to check if a tool supports streaming
 */
export function hasStreamingSupport(tool) {
    return tool !== null &&
        typeof tool === 'object' &&
        'fieldsToCheck' in tool &&
        'listsToCheck' in tool &&
        Array.isArray(tool.fieldsToCheck) &&
        Array.isArray(tool.listsToCheck);
}
export default ToolBase;
